import {Navigate} from 'react-router-dom';

import {Module, ModuleItem} from '@dms/api/shared';
import {moduleKeys} from '@dms/config';
import {featureFlags} from '@dms/feature-flags';
import {warehouseRoutes} from '@dms/routes';
import {permissions, warehousePermissions} from '@dms/shared';
import {ArticleDetail, ArticleList} from '@dms/warehouse/article';
import {CoreExchangeDetail, CoreExchangeList} from '@dms/warehouse/core-exchange';
import {CostCorrectionDetail} from '@dms/warehouse/cost-correction';
import {DeliveryNoteDetail} from '@dms/warehouse/delivery-note';
import {
  DirectSalesCreate,
  DirectSalesDetail,
  DirectSalesList,
  DirectSalesVariantChange,
} from '@dms/warehouse/direct-sale';
import {DeliveryNoteDocumentPreviewDialog} from '@dms/warehouse/document-extraction';
import {FastTrackIssuesCreate, FastTrackIssuesDetail} from '@dms/warehouse/fast_track';
import {HyundaiMobisIrisReport} from '@dms/warehouse/hyundai-mobis-iris-reporting';
import {IssueNoteCorrectionDetail, IssueNoteDetail} from '@dms/warehouse/issue-note';
import {PriceList} from '@dms/warehouse/price-list';
import {
  ReceiveNoteCorrectionDetail,
  ReceiveNoteDetail,
  ReceiveNoteLists,
} from '@dms/warehouse/receive-note';
import {
  AbcXyzAnalysisReport,
  InventoryClosingReport,
  WarehouseCharts,
  WarehouseReport,
} from '@dms/warehouse/reporting';
import {
  ServiceOrderIssueCreate,
  ServiceOrderIssueDetail,
  ServiceOrderIssueList,
  ServiceOrderReturnsCreate,
  ServiceOrderReturnsDetail,
} from '@dms/warehouse/service-order';
import {SupplierOrderDetail, SupplierOrders} from '@dms/warehouse/supplier-order';
import {TransferOrderCreate, TransferOrderDetail} from '@dms/warehouse/transfers';
import {UpdateJobDetail} from '@dms/warehouse/update-job';

import {WAREHOUSE_CHART_TABS} from './warehouseChartTabs';
import {WarehouseChartUnavailable} from './WarehouseChartUnavailable';

const content: ModuleItem[] = [
  {
    id: 'warehouse-directSales',
    routerProps: {
      path: warehouseRoutes.directSalesList,
      element: <DirectSalesList />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.directSales',
      icon: 'sidebar/warehouse_direct_sales',
    },
    requiredPermission: warehousePermissions.directSaleRead,
  },
  {
    id: 'warehouse-directSalesCreate',
    routerProps: {
      path: warehouseRoutes.directSalesCreate,
      element: <DirectSalesCreate />,
    },
    requiredPermission: warehousePermissions.directSaleRead,
  },
  {
    id: 'warehouse-directSalesVariantChange',
    routerProps: {
      path: warehouseRoutes.directSalesVariantChange,
      element: <DirectSalesVariantChange />,
    },
    requiredPermission: warehousePermissions.directSaleRead,
  },
  {
    id: 'warehouse-directSalesDetailOverview',
    routerProps: {
      path: warehouseRoutes.directSalesDetailOverview,
      element: <DirectSalesDetail />,
    },
    requiredPermission: warehousePermissions.directSaleRead,
  },
  {
    id: 'warehouse-directSalesDetailDocuments',
    routerProps: {
      path: warehouseRoutes.directSalesDetailDocuments,
      element: <DirectSalesDetail />,
    },
    requiredPermission: warehousePermissions.directSaleRead,
  },
  {
    id: 'warehouse-directSalesDetailVehicle',
    routerProps: {
      path: warehouseRoutes.directSalesDetailVehicle,
      element: <DirectSalesDetail />,
    },
    requiredPermission: warehousePermissions.directSaleRead,
  },
  {
    id: 'warehouse-directSalesDetailCustomer',
    routerProps: {
      path: warehouseRoutes.directSalesDetailCustomer,
      element: <DirectSalesDetail />,
    },
    requiredPermission: warehousePermissions.directSaleRead,
  },
  {
    id: 'warehouse-directSalesDetailChecklists',
    routerProps: {
      path: warehouseRoutes.directSalesDetailChecklists,
      element: <DirectSalesDetail />,
    },
    requiredPermission: warehousePermissions.directSaleRead,
  },
  {
    id: 'warehouse-directSalesDetailTasks',
    routerProps: {
      path: warehouseRoutes.directSalesDetailTasks,
      element: <DirectSalesDetail />,
    },
    requiredPermission: permissions.readTask,
  },
  {
    id: 'warehouse-directSalesDetailCustomNotices',
    routerProps: {
      path: warehouseRoutes.directSalesDetailCustomNotices,
      element: <DirectSalesDetail />,
    },
    requiredPermission: warehousePermissions.directSaleRead,
    requiredFeatureFlag: featureFlags.CUSTOM_NOTICES,
  },
  // === Materials Group ===
  {
    id: 'warehouse-group-materials',
    routerProps: {
      path: '/warehouses/group-materials',
    },
    layoutProps: {
      title: 'page.warehouse.label.groupMaterials',
      icon: 'sidebar/material',
    },
  },
  {
    id: 'warehouse-articleList',
    routerProps: {
      path: warehouseRoutes.articleList,
      element: <ArticleList />,
    },
    layoutProps: {
      title: 'page.warehouse.label.articleList',
      icon: 'sidebar/warehouse_articles',
    },
    requiredPermission: warehousePermissions.articleRead,
    parentId: 'warehouse-group-materials',
  },
  {
    id: 'warehouse-articleDetailOverview',
    routerProps: {
      path: warehouseRoutes.articleDetailOverview,
      element: <ArticleDetail />,
    },
    requiredPermission: warehousePermissions.articleRead,
  },
  {
    id: 'warehouse-articleDetailAvailability',
    routerProps: {
      path: warehouseRoutes.articleDetailAvailability,
      element: <ArticleDetail />,
    },
    requiredPermission: warehousePermissions.articleRead,
  },
  {
    id: 'warehouse-articleDetailLocations',
    routerProps: {
      path: warehouseRoutes.articleDetailLocations,
      element: <ArticleDetail />,
    },
    requiredPermission: warehousePermissions.articleRead,
  },
  {
    id: 'warehouse-articleDetailReceipts',
    routerProps: {
      path: warehouseRoutes.articleDetailReceipts,
      element: <ArticleDetail />,
    },
    requiredPermission: warehousePermissions.articleRead,
  },
  {
    id: 'warehouse-articleDetailMovements',
    routerProps: {
      path: warehouseRoutes.articleDetailMovements,
      element: <ArticleDetail />,
    },
    requiredPermission: warehousePermissions.articleRead,
  },
  {
    id: 'warehouse-articleDetailHistory',
    routerProps: {
      path: warehouseRoutes.articleDetailHistory,
      element: <ArticleDetail />,
    },
    requiredPermission: warehousePermissions.articleRead,
  },
  {
    id: 'warehouse-articleDetailReservations',
    routerProps: {
      path: warehouseRoutes.articleDetailReservations,
      element: <ArticleDetail />,
    },
    requiredPermission: warehousePermissions.articleRead,
  },
  {
    id: 'warehouse-articleDetailSupplierArticles',
    routerProps: {
      path: warehouseRoutes.articleDetailSupplierArticles,
      element: <ArticleDetail />,
    },
    requiredPermission: warehousePermissions.articleRead,
  },
  {
    id: 'warehouse-articleDetailUpdateJobsLog',
    routerProps: {
      path: warehouseRoutes.articleDetailUpdateJobsLog,
      element: <ArticleDetail />,
    },
    requiredPermission: warehousePermissions.articleRead,
  },
  {
    id: 'warehouse-articleDetailDocuments',
    routerProps: {
      path: warehouseRoutes.articleDetailDocuments,
      element: <ArticleDetail />,
    },
    requiredPermission: warehousePermissions.articleRead,
  },
  {
    id: 'warehouse-articleDetailChecklists',
    routerProps: {
      path: warehouseRoutes.articleDetailChecklists,
      element: <ArticleDetail />,
    },
    requiredPermission: warehousePermissions.articleRead,
  },
  {
    id: 'warehouse-priceList',
    routerProps: {
      path: warehouseRoutes.priceList,
      element: <PriceList />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.supplierPriceLists',
      icon: 'sidebar/warehouse_price_list',
    },
    parentId: 'warehouse-group-materials',
  },
  {
    id: 'warehouse-updateJobs',
    routerProps: {
      path: warehouseRoutes.updateJobsList,
      element: <PriceList />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.articleUpdateJobs',
      icon: 'sidebar/warehouse_article_update_jobs',
    },
    requiredPermission: warehousePermissions.articleUpdateJobRead,
    parentId: 'warehouse-group-materials',
  },
  {
    id: 'warehouse-updateJobs-create',
    routerProps: {
      path: warehouseRoutes.updateJobCreate,
      element: <UpdateJobDetail />,
    },
    requiredPermission: warehousePermissions.articleUpdateJobRead,
  },
  {
    id: 'warehouse-updateJobsDetailOverview',
    routerProps: {
      path: warehouseRoutes.updateJobDetailOverview,
      element: <UpdateJobDetail />,
    },
    requiredPermission: warehousePermissions.articleUpdateJobRead,
  },
  {
    id: 'warehouse-updateJobsDetailUpdateList',
    routerProps: {
      path: warehouseRoutes.updateJobDetailUpdateList,
      element: <UpdateJobDetail />,
    },
    requiredPermission: warehousePermissions.articleUpdateJobRead,
  },
  {
    id: 'warehouse-updateJobsDetailUpdateResults',
    routerProps: {
      path: warehouseRoutes.updateJobDetailUpdateResults,
      element: <UpdateJobDetail />,
    },
    requiredPermission: warehousePermissions.articleUpdateJobRead,
  },
  // === Dispatch Group ===
  {
    id: 'warehouse-group-dispatch',
    routerProps: {
      path: '/warehouses/group-dispatch',
    },
    layoutProps: {
      title: 'page.warehouse.label.groupDispatch',
      icon: 'sidebar/dispatch',
    },
  },
  {
    id: 'warehouse-serviceOrderIssues',
    routerProps: {
      path: warehouseRoutes.serviceOrderIssues,
      element: <ServiceOrderIssueList />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.serviceOrderIssues',
      icon: 'sidebar/warehouse_service_order_issues',
    },
    parentId: 'warehouse-group-dispatch',
  },
  {
    id: 'warehouse-serviceOrderIssueCreate',
    routerProps: {
      path: warehouseRoutes.serviceOrderIssueCreate,
      element: <ServiceOrderIssueCreate />,
    },
  },
  {
    id: 'warehouse-serviceOrderIssueDetailOverview',
    routerProps: {
      path: warehouseRoutes.serviceOrderIssueDetailOverview,
      element: <ServiceOrderIssueDetail />,
    },
  },
  {
    id: 'warehouse-serviceOrderIssueDetailIssueNotes',
    routerProps: {
      path: warehouseRoutes.serviceOrderIssueDetailIssueNotes,
      element: <ServiceOrderIssueDetail />,
    },
  },
  {
    id: 'warehouse-serviceOrderIssueDetailDocuments',
    routerProps: {
      path: warehouseRoutes.serviceOrderIssueDetailDocuments,
      element: <ServiceOrderIssueDetail />,
    },
  },
  {
    id: 'warehouse-serviceOrderIssueDetailChecklists',
    routerProps: {
      path: warehouseRoutes.serviceOrderIssueDetailChecklists,
      element: <ServiceOrderIssueDetail />,
    },
  },
  {
    id: 'warehouse-serviceOrderReturns',
    routerProps: {
      path: warehouseRoutes.serviceOrderReturns,
      element: <ServiceOrderIssueList />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.serviceOrderReturns',
      icon: 'sidebar/warehouse_service_order_returns',
    },
    requiredPermission: warehousePermissions.returnToWarehouseRead,
    parentId: 'warehouse-group-dispatch',
  },
  {
    id: 'warehouse-serviceOrderReturnsCreate',
    routerProps: {
      path: warehouseRoutes.serviceOrderReturnsCreate,
      element: <ServiceOrderReturnsCreate />,
    },
    requiredPermission: warehousePermissions.returnToWarehouseCreate,
  },
  {
    id: 'warehouse-serviceOrderReturnsDetailOverview',
    routerProps: {
      path: warehouseRoutes.serviceOrderReturnsDetailOverview,
      element: <ServiceOrderReturnsDetail />,
    },
    requiredPermission: warehousePermissions.returnToWarehouseRead,
  },
  {
    id: 'warehouse-serviceOrderReturnsDetailDocuments',
    routerProps: {
      path: warehouseRoutes.serviceOrderReturnsDetailDocuments,
      element: <ServiceOrderReturnsDetail />,
    },
    requiredPermission: warehousePermissions.returnToWarehouseRead,
  },
  {
    id: 'warehouse-serviceOrderReturnsDetailChecklists',
    routerProps: {
      path: warehouseRoutes.serviceOrderReturnsDetailChecklists,
      element: <ServiceOrderReturnsDetail />,
    },
    requiredPermission: warehousePermissions.returnToWarehouseRead,
  },
  {
    id: 'warehouse-issueNotes',
    routerProps: {
      path: warehouseRoutes.issueNotes,
      element: <ServiceOrderIssueList />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.issueNotes',
      icon: 'sidebar/warehouse_issue_note',
    },
    parentId: 'warehouse-group-dispatch',
  },
  {
    id: 'warehouse-issueNoteDetailOverview',
    routerProps: {
      path: warehouseRoutes.issueNoteDetailOverview,
      element: <IssueNoteDetail />,
    },
  },
  {
    id: 'warehouse-issueNoteDetailIssueNoteCorrections',
    routerProps: {
      path: warehouseRoutes.issueNoteDetailIssueNoteCorrections,
      element: <IssueNoteDetail />,
    },
  },
  {
    id: 'warehouse-issueNoteDetailCostCorrections',
    routerProps: {
      path: warehouseRoutes.issueNoteDetailCostCorrections,
      element: <IssueNoteDetail />,
    },
  },
  {
    id: 'warehouse-issueNoteDetailDocuments',
    routerProps: {
      path: warehouseRoutes.issueNoteDetailDocuments,
      element: <IssueNoteDetail />,
    },
  },
  {
    id: 'warehouse-issueNoteCorrections',
    routerProps: {
      path: warehouseRoutes.issueNoteCorrections,
      element: <ServiceOrderIssueList />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.issueNoteCorrections',
      icon: 'sidebar/warehouse_issue_notes_corrections',
    },
    parentId: 'warehouse-group-dispatch',
  },
  {
    id: 'warehouse-issueNoteCorrectionDetailOverview',
    routerProps: {
      path: warehouseRoutes.issueNoteCorrectionDetailOverview,
      element: <IssueNoteCorrectionDetail />,
    },
  },
  {
    id: 'warehouse-issueNoteCorrectionDetailDocuments',
    routerProps: {
      path: warehouseRoutes.issueNoteCorrectionDetailDocuments,
      element: <IssueNoteCorrectionDetail />,
    },
  },
  {
    id: 'warehouse-costCorrections',
    routerProps: {
      path: warehouseRoutes.costCorrections,
      element: <ServiceOrderIssueList />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.costCorrections',
      icon: 'sidebar/warehouse_cost_corrections',
    },
    parentId: 'warehouse-group-dispatch',
  },
  {
    id: 'warehouse-costCorrectionDetailOverview',
    routerProps: {
      path: warehouseRoutes.costCorrectionDetailOverview,
      element: <CostCorrectionDetail />,
    },
  },
  {
    id: 'warehouse-costCorrectionDetailDocuments',
    routerProps: {
      path: warehouseRoutes.costCorrectionDetailDocuments,
      element: <CostCorrectionDetail />,
    },
  },
  {
    id: 'warehouse-coreExchanges',
    routerProps: {
      path: warehouseRoutes.coreExchanges,
      element: <CoreExchangeList />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.coreExchanges',
      icon: 'sidebar/warehouse_service_order_returns',
    },
    requiredPermission: warehousePermissions.coreExchangeRead,
    parentId: 'warehouse-group-dispatch',
  },
  {
    id: 'warehouse-coreExchangeDetailOverview',
    routerProps: {
      path: warehouseRoutes.coreExchangeDetailOverview,
      element: <CoreExchangeDetail />,
    },
    requiredPermission: warehousePermissions.coreExchangeRead,
  },
  {
    id: 'warehouse-fastTrackIssues',
    routerProps: {
      path: warehouseRoutes.fastTrackIssues,
      element: <ServiceOrderIssueList />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.fastTrackIssues',
      icon: 'sidebar/warehouse_fast_track',
    },
    requiredPermission: warehousePermissions.fastTrackIssueRead,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_FAST_TRACK_ISSUE,
    parentId: 'warehouse-group-dispatch',
  },
  {
    id: 'warehouse-transfers',
    routerProps: {
      path: warehouseRoutes.transfers,
      element: <ServiceOrderIssueList />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.transfers',
      icon: 'sidebar/warehouse_transfer',
    },
    requiredPermission: warehousePermissions.transferOrderRead,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_TRANSFER_ORDER,
    parentId: 'warehouse-group-dispatch',
  },
  {
    id: 'warehouse-fastTrackIssuesCreate',
    routerProps: {
      path: warehouseRoutes.fastTrackIssuesCreate,
      element: <FastTrackIssuesCreate />,
    },
    requiredPermission: warehousePermissions.fastTrackIssueCreate,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_FAST_TRACK_ISSUE,
  },
  {
    id: 'warehouse-fastTrackIssuesDetailOverview',
    routerProps: {
      path: warehouseRoutes.fastTrackIssuesDetailOverview,
      element: <FastTrackIssuesDetail />,
    },
    requiredPermission: warehousePermissions.fastTrackIssueRead,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_FAST_TRACK_ISSUE,
  },
  {
    id: 'warehouse-fastTrackIssuesDetailIssueNotes',
    routerProps: {
      path: warehouseRoutes.fastTrackIssuesDetailIssueNotes,
      element: <FastTrackIssuesDetail />,
    },
    requiredPermission: warehousePermissions.fastTrackIssueRead,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_FAST_TRACK_ISSUE,
  },
  {
    id: 'warehouse-fastTrackIssuesDetailDocuments',
    routerProps: {
      path: warehouseRoutes.fastTrackIssuesDetailDocuments,
      element: <FastTrackIssuesDetail />,
    },
    requiredPermission: warehousePermissions.fastTrackIssueRead,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_FAST_TRACK_ISSUE,
  },
  {
    id: 'warehouse-fastTrackIssuesDetailChecklists',
    routerProps: {
      path: warehouseRoutes.fastTrackIssuesDetailChecklists,
      element: <FastTrackIssuesDetail />,
    },
    requiredPermission: warehousePermissions.fastTrackIssueRead,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_FAST_TRACK_ISSUE,
  },

  {
    id: 'warehouse-transfer-order-create',
    routerProps: {
      path: warehouseRoutes.transferOrderCreate,
      element: <TransferOrderCreate />,
    },
    requiredPermission: warehousePermissions.transferOrderCreate,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_TRANSFER_ORDER,
  },
  {
    id: 'warehouse-transfer-order-draft',
    routerProps: {
      path: warehouseRoutes.transferOrderDraft,
      element: <TransferOrderDetail />,
    },
    requiredPermission: warehousePermissions.transferOrderRead,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_TRANSFER_ORDER,
  },
  {
    id: 'warehouse-transfer-order-detail-overview',
    routerProps: {
      path: warehouseRoutes.transferOrderDetailOverview,
      element: <TransferOrderDetail />,
    },
    requiredPermission: warehousePermissions.transferOrderRead,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_TRANSFER_ORDER,
  },
  {
    id: 'warehouse-transfer-order-detail-issue-notes',
    routerProps: {
      path: warehouseRoutes.transferOrderDetailIssueNotes,
      element: <TransferOrderDetail />,
    },
    requiredPermission: warehousePermissions.transferOrderRead,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_TRANSFER_ORDER,
  },
  {
    id: 'warehouse-transfer-order-detail-documents',
    routerProps: {
      path: warehouseRoutes.transferOrderDetailDocuments,
      element: <TransferOrderDetail />,
    },
    requiredPermission: warehousePermissions.transferOrderRead,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_TRANSFER_ORDER,
  },
  {
    id: 'warehouse-transfer-order-detail-checklists',
    routerProps: {
      path: warehouseRoutes.transferOrderDetailChecklists,
      element: <TransferOrderDetail />,
    },
    requiredPermission: warehousePermissions.transferOrderRead,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_TRANSFER_ORDER,
  },
  // === Receive Group ===
  {
    id: 'warehouse-group-receive',
    routerProps: {
      path: '/warehouses/group-receive',
    },
    layoutProps: {
      title: 'page.warehouse.label.groupReceive',
      icon: 'sidebar/receive',
    },
  },
  {
    id: 'warehouse-deliveryNoteList',
    routerProps: {
      path: warehouseRoutes.deliveryNoteList,
      element: <ReceiveNoteLists />,
    },
    requiredPermission: warehousePermissions.deliveryNoteRead,
    layoutProps: {
      title: 'page.warehouse.labels.deliveryNotes',
      icon: 'sidebar/warehouse_delivery_notes',
    },
    parentId: 'warehouse-group-receive',
  },
  {
    id: 'warehouse-deliveryNoteDetailOverview',
    routerProps: {
      path: warehouseRoutes.deliveryNoteDetailOverview,
      element: <DeliveryNoteDetail />,
    },
    requiredPermission: warehousePermissions.deliveryNoteRead,
  },
  {
    id: 'warehouse-deliveryNoteDetailDocuments',
    routerProps: {
      path: warehouseRoutes.deliveryNoteDetailDocuments,
      element: <DeliveryNoteDetail />,
    },
    requiredPermission: warehousePermissions.deliveryNoteRead,
  },
  {
    id: 'warehouse-deliveryNoteDetailChecklists',
    routerProps: {
      path: warehouseRoutes.deliveryNoteDetailChecklists,
      element: <DeliveryNoteDetail />,
    },
    requiredPermission: warehousePermissions.deliveryNoteRead,
  },
  {
    id: 'warehouse-deliveryNoteDocumentPreview',
    routerProps: {
      path: warehouseRoutes.deliveryNoteDocumentPreview,
      element: <DeliveryNoteDocumentPreviewDialog />,
    },
    requiredPermission: warehousePermissions.deliveryNoteAiExtract,
    requiredFeatureFlag: featureFlags.AFS_WAREHOUSE_DELIVERY_NOTE_DOCUMENT_PREVIEW,
  },
  {
    id: 'warehouse-receiveNoteList',
    routerProps: {
      path: warehouseRoutes.receiveNoteList,
      element: <ReceiveNoteLists />,
    },
    requiredPermission: warehousePermissions.receiveNoteRead,
    layoutProps: {
      title: 'page.warehouse.labels.receiptNotes',
      icon: 'sidebar/warehouse_receive_notes',
    },
    parentId: 'warehouse-group-receive',
  },
  {
    id: 'warehouse-receiveNoteCreate',
    routerProps: {
      path: warehouseRoutes.receiveNoteCreate,
      element: <ReceiveNoteDetail />,
    },
    requiredPermission: warehousePermissions.receiveNoteCreate,
  },
  {
    id: 'warehouse-receiveNoteDetailOverview',
    routerProps: {
      path: warehouseRoutes.receiveNoteDetailOverview,
      element: <ReceiveNoteDetail />,
    },
    requiredPermission: warehousePermissions.receiveNoteRead,
  },
  {
    id: 'warehouse-receiveNoteDetailDocuments',
    routerProps: {
      path: warehouseRoutes.receiveNoteDetailDocuments,
      element: <ReceiveNoteDetail />,
    },
    requiredPermission: warehousePermissions.receiveNoteRead,
  },
  {
    id: 'warehouse-receiveNoteDetailChecklists',
    routerProps: {
      path: warehouseRoutes.receiveNoteDetailChecklists,
      element: <ReceiveNoteDetail />,
    },
    requiredPermission: warehousePermissions.receiveNoteRead,
  },
  {
    id: 'warehouse-receiveNoteCorrectionsList',
    routerProps: {
      path: warehouseRoutes.receiveNoteCorrectionsList,
      element: <ReceiveNoteLists />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.receiptNoteCorrections',
      icon: 'sidebar/warehouse_receive_note_corrections',
    },
    requiredPermission: warehousePermissions.receiveNoteCorrectionRead,
    parentId: 'warehouse-group-receive',
  },
  {
    id: 'warehouse-receiveNoteCorrectionCreate',
    routerProps: {
      path: warehouseRoutes.receiveNoteCorrectionCreate,
      element: <ReceiveNoteCorrectionDetail />,
    },
    requiredPermission: warehousePermissions.receiveNoteCorrectionRead,
  },
  {
    id: 'warehouse-receiveNoteCorrectionDetailOverview',
    routerProps: {
      path: warehouseRoutes.receiveNoteCorrectionDetailOverview,
      element: <ReceiveNoteCorrectionDetail />,
    },
    requiredPermission: warehousePermissions.receiveNoteCorrectionRead,
  },
  {
    id: 'warehouse-receiveNoteCorrectionDetailDocuments',
    routerProps: {
      path: warehouseRoutes.receiveNoteCorrectionDetailDocuments,
      element: <ReceiveNoteCorrectionDetail />,
    },
    requiredPermission: warehousePermissions.receiveNoteCorrectionRead,
  },
  {
    id: 'warehouse-receiveNoteCorrectionDetailChecklists',
    routerProps: {
      path: warehouseRoutes.receiveNoteCorrectionDetailChecklists,
      element: <ReceiveNoteCorrectionDetail />,
    },
    requiredPermission: warehousePermissions.receiveNoteCorrectionRead,
  },
  // === Order Group ===
  {
    id: 'warehouse-group-order',
    routerProps: {
      path: '/warehouses/group-order',
    },
    layoutProps: {
      title: 'page.warehouse.label.groupOrder',
      icon: 'action/shopping_cart',
    },
  },
  {
    id: 'warehouse-purchaseList',
    routerProps: {
      path: warehouseRoutes.purchaseList,
      element: <SupplierOrders />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.purchaseList',
      icon: 'sidebar/warehouse_purchase_list',
    },
    requiredPermission: warehousePermissions.supplierOrderRead,
    parentId: 'warehouse-group-order',
  },
  {
    id: 'warehouse-supplierOrders',
    routerProps: {
      path: warehouseRoutes.supplierOrderList,
      element: <SupplierOrders hasNewOrderAction />,
    },
    layoutProps: {
      title: 'page.warehouse.labels.supplierOrders',
      icon: 'sidebar/warehouse_supplier_orders',
    },
    requiredPermission: warehousePermissions.supplierOrderRead,
    parentId: 'warehouse-group-order',
  },
  {
    id: 'warehouse-supplierOrders-compat-redirect',
    routerProps: {
      path: warehouseRoutes.supplierOrders,
      element: <Navigate replace to={warehouseRoutes.supplierOrderList} />,
    },
  },
  {
    id: 'warehouse-supplierOrderDetailOverview',
    routerProps: {
      path: warehouseRoutes.supplierOrderDetailOverview,
      element: <SupplierOrderDetail />,
    },
    requiredPermission: warehousePermissions.supplierOrderRead,
  },
  {
    id: 'warehouse-supplierOrderDetailDocuments',
    routerProps: {
      path: warehouseRoutes.supplierOrderDetailDocuments,
      element: <SupplierOrderDetail />,
    },
    requiredPermission: warehousePermissions.supplierOrderRead,
  },
  {
    id: 'warehouse-supplierOrderDetailChecklists',
    routerProps: {
      path: warehouseRoutes.supplierOrderDetailChecklists,
      element: <SupplierOrderDetail />,
    },
    requiredPermission: warehousePermissions.supplierOrderRead,
  },
  {
    id: 'warehouse-reporting-group',
    routerProps: {
      path: warehouseRoutes.reporting,
    },
    layoutProps: {
      title: 'entity.warehouse.labels.reporting',
      icon: 'sidebar/analytics',
    },
    requiredPermissionAtLeastOne: [
      warehousePermissions.warehouseReportingRead,
      warehousePermissions.warehouseReportingHyundaiMobisIrisRead,
      warehousePermissions.warehouseReportingDailyStockTakeRead,
    ],
  },
  {
    id: 'warehouse-reporting',
    parentId: 'warehouse-reporting-group',
    routerProps: {
      path: warehouseRoutes.reporting,
      element: <WarehouseReport />,
    },
    layoutProps: {
      title: 'analytics.label.warehouseReports',
      icon: 'sidebar/analytics',
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
  },
  {
    id: 'warehouse-reporting-tab',
    routerProps: {
      path: warehouseRoutes.reportingTab,
      element: <WarehouseReport />,
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
  },
  {
    id: 'warehouse-reporting-charts-group',
    parentId: 'warehouse-reporting-group',
    routerProps: {
      path: warehouseRoutes.reportingCharts,
      element: <WarehouseCharts />,
    },
    layoutProps: {title: 'analytics.label.reportingCharts', icon: 'sidebar/analytics'},
    requiredPermission: warehousePermissions.warehouseReportingRead,
    requiredFeatureFlag: (resolve) =>
      WAREHOUSE_CHART_TABS.some((chart) => resolve(chart.featureFlag)),
  },
  {
    id: 'warehouse-reporting-hyundai-mobis-iris',
    parentId: 'warehouse-reporting-group',
    routerProps: {
      path: warehouseRoutes.reportingHyundaiMobisIris,
      element: <HyundaiMobisIrisReport />,
    },
    layoutProps: {
      title: 'analytics.label.hyundaiMobisIris',
      icon: 'sidebar/analytics',
    },
    requiredPermission: warehousePermissions.warehouseReportingHyundaiMobisIrisRead,
    dynamicAccess: 'hyundaiMobisIrisReporting',
  },
  {
    id: 'warehouse-reporting-inventory-closing',
    parentId: 'warehouse-reporting-group',
    routerProps: {
      path: warehouseRoutes.reportingInventoryClosing,
      element: <InventoryClosingReport />,
    },
    layoutProps: {
      title: 'analytics.label.inventoryClosing',
      icon: 'sidebar/analytics',
    },
    requiredPermission: warehousePermissions.warehouseReportingDailyStockTakeRead,
  },
  {
    id: 'warehouse-reporting-inventory-closing-monthly',
    routerProps: {
      path: warehouseRoutes.reportingInventoryClosingMonthly,
      element: <InventoryClosingReport />,
    },
    requiredPermission: warehousePermissions.warehouseReportingDailyStockTakeRead,
  },
  {
    id: 'warehouse-reporting-abc-xyz-analysis',
    parentId: 'warehouse-reporting-group',
    routerProps: {
      path: warehouseRoutes.reportingAbcXyzAnalysis,
      element: <AbcXyzAnalysisReport />,
    },
    layoutProps: {
      title: 'analytics.label.abcXyzAnalysis',
      icon: 'sidebar/analytics',
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
  },
  {
    id: 'warehouse-chart-direct-sales',
    routerProps: {
      path: warehouseRoutes.reportingChartDirectSales,
      element: <WarehouseCharts />,
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_WAREHOUSE_DIRECT_SALES_REPORTING,
  },
  {
    id: 'warehouse-chart-redirect-direct-sales',
    routerProps: {
      path: `${warehouseRoutes.reporting}/direct-sales-performance`,
      element: <Navigate to={warehouseRoutes.reportingChartDirectSales} replace />,
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_WAREHOUSE_DIRECT_SALES_REPORTING,
  },
  // The chart routes below are written out literally on purpose. The DMS route-catalog
  // generator only resolves a literal string `id` plus a direct `warehouseRoutes.<member>`
  // path reference, so items produced by a `map`/`flatMap` never reach the catalog and the
  // AI assistant cannot deep-link to them. Keep them in sync with WAREHOUSE_CHART_TABS.
  {
    id: 'warehouse-chart-inventory-value',
    routerProps: {
      path: warehouseRoutes.reportingChartInventory,
      element: <WarehouseCharts />,
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_WAREHOUSE_INVENTORY_VALUE_REPORTING,
  },
  {
    id: 'warehouse-chart-redirect-inventory-value',
    routerProps: {
      path: `${warehouseRoutes.reporting}/inventory-value`,
      element: <Navigate to={warehouseRoutes.reportingChartInventory} replace />,
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_WAREHOUSE_INVENTORY_VALUE_REPORTING,
  },
  {
    id: 'warehouse-chart-dead-stock',
    routerProps: {
      path: warehouseRoutes.reportingChartDeadStock,
      element: <WarehouseCharts />,
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_WAREHOUSE_DEAD_STOCK_REPORTING,
  },
  {
    id: 'warehouse-chart-redirect-dead-stock',
    routerProps: {
      path: `${warehouseRoutes.reporting}/dead-stock`,
      element: <Navigate to={warehouseRoutes.reportingChartDeadStock} replace />,
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_WAREHOUSE_DEAD_STOCK_REPORTING,
  },
  {
    id: 'warehouse-chart-warehouse-movements',
    routerProps: {
      path: warehouseRoutes.reportingChartMovements,
      element: <WarehouseCharts />,
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_WAREHOUSE_MOVEMENTS_REPORTING,
  },
  {
    id: 'warehouse-chart-redirect-warehouse-movements',
    routerProps: {
      path: `${warehouseRoutes.reporting}/warehouse-movements`,
      element: <Navigate to={warehouseRoutes.reportingChartMovements} replace />,
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
    requiredFeatureFlag: featureFlags.ADVANCED_WAREHOUSE_MOVEMENTS_REPORTING,
  },
  // Keep a safe destination when the active chart is switched off locally.
  // Exactly one of the normal route and its fallback is registered.
  ...WAREHOUSE_CHART_TABS.flatMap<ModuleItem>((chart) =>
    [chart.path, `${warehouseRoutes.reporting}/${chart.id}`].map<ModuleItem>((path, index) => ({
      id: `warehouse-chart-unavailable-${chart.id}-${index}`,
      routerProps: {path, element: <WarehouseChartUnavailable />},
      requiredPermission: warehousePermissions.warehouseReportingRead,
      requiredFeatureFlag: (resolve) => !resolve(chart.featureFlag),
    }))
  ),
  {
    id: 'warehouse-reporting-charts-unavailable',
    routerProps: {
      path: warehouseRoutes.reportingCharts,
      element: <Navigate to={warehouseRoutes.reporting} replace />,
    },
    requiredPermission: warehousePermissions.warehouseReportingRead,
    requiredFeatureFlag: (resolve) =>
      !WAREHOUSE_CHART_TABS.some((chart) => resolve(chart.featureFlag)),
  },
];

export const WarehouseModule: Module = {
  id: moduleKeys.warehouse,
  layoutProps: {
    icon: 'sidebar/warehouse',
    title: 'module.warehouse.title',
  },
  routerProps: {
    path: warehouseRoutes.articleList,
  },
  requiredPermission: warehousePermissions.articleRead,
  requiredPermissionAtLeastOne: [
    warehousePermissions.articleRead,
    warehousePermissions.warehouseReportingRead,
    warehousePermissions.warehouseReportingHyundaiMobisIrisRead,
    warehousePermissions.warehouseReportingDailyStockTakeRead,
  ],
  content,
};
